import { useCallback, useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { CreateInFolderDropdown } from '@/components/ui/create-in-folder-menu'
import { dispatchCreateInFolder } from '@/lib/create-in-folder'
import { getTreeDrillDownView, sanitizeTreeFocusPath } from '@/lib/documents-tree-path'
import { workspaceCrumbParentButtonClassName } from '@/lib/workspace-crumb-styles'
import { ExplorerInlineFolderNode, type ExplorerFolderTreeNode } from '@/components/layout/explorer-inline-folder-node'
import type { OpenTabIndex } from '@/lib/open-tab-match'
import { testIds } from '@/lib/test-ids'

export type { ExplorerFolderTreeNode }

const NO_NODES: ExplorerFolderTreeNode[] = []

type DocumentsExplorerInlineSectionProps = {
  treeKey: string
  treeRoot: string
  wsColor: string
  workspaceId?: string
  nodes: ExplorerFolderTreeNode[] | undefined
  /** True only when there is no cached tree yet (first load). */
  loading: boolean
  /** True when refetching but previous nodes are still shown. */
  refreshing?: boolean
  focusSegments: string[]
  onFocusSegments: (segments: string[]) => void
  activeDocumentPath: string | null
  onFolderClick: (path: string, event?: React.MouseEvent) => void
  onFileClick: (path: string, event?: React.MouseEvent) => void
  onFileDoubleClick?: (path: string) => void
  onContextMenu?: (node: ExplorerFolderTreeNode, event: React.MouseEvent) => void
  openTabPaths?: string[]
  /** Path + id index for open-echo (UUID tabs without filePath). */
  openTabIndex?: OpenTabIndex
  /** Root empty-state copy (default: No folders found). */
  emptyLabel?: string
}

export function DocumentsExplorerInlineSection({
  treeKey,
  treeRoot,
  wsColor,
  workspaceId,
  nodes,
  loading,
  refreshing = false,
  focusSegments,
  onFocusSegments,
  activeDocumentPath,
  onFolderClick,
  onFileClick,
  onFileDoubleClick,
  onContextMenu,
  openTabPaths = [],
  openTabIndex,
  emptyLabel = 'No folders found',
}: DocumentsExplorerInlineSectionProps) {
  const list = nodes ?? NO_NODES
  const focusKey = focusSegments.join('\0')

  const listRef = useRef(list)
  listRef.current = list

  const onFocusSegmentsRef = useRef(onFocusSegments)
  onFocusSegmentsRef.current = onFocusSegments
  const onFolderClickRef = useRef(onFolderClick)
  onFolderClickRef.current = onFolderClick
  const onFileClickRef = useRef(onFileClick)
  onFileClickRef.current = onFileClick
  const onFileDoubleClickRef = useRef(onFileDoubleClick)
  onFileDoubleClickRef.current = onFileDoubleClick
  const onContextMenuRef = useRef(onContextMenu)
  onContextMenuRef.current = onContextMenu
  useEffect(() => {
    if (loading || nodes === undefined) return
    if (!list.length) {
      if (focusSegments.length > 0) onFocusSegmentsRef.current([])
      return
    }
    const safe = sanitizeTreeFocusPath(list, focusSegments)
    if (safe.length !== focusSegments.length || safe.some((s, i) => s !== focusSegments[i])) {
      onFocusSegmentsRef.current(safe)
    }
  }, [treeKey, focusKey, list, focusSegments, loading, nodes])

  const handleFocusCrumb = useCallback((pathUpToHere: string[]) => {
    onFocusSegmentsRef.current(sanitizeTreeFocusPath(listRef.current, pathUpToHere))
  }, [])

  const handleNodeClick = useCallback((path: string, type: 'folder' | 'file', e: React.MouseEvent) => {
    if (type === 'folder') onFolderClickRef.current(path, e)
    else onFileClickRef.current(path, e)
  }, [])

  const handleNodeDoubleClick = useCallback((path: string, type: 'folder' | 'file', _e: React.MouseEvent) => {
    if (type === 'file') onFileDoubleClickRef.current?.(path)
  }, [])

  const handleContextMenu = useCallback((node: ExplorerFolderTreeNode, event: React.MouseEvent) => {
    onContextMenuRef.current?.(node, event)
  }, [])

  const handleDrillFocus = useCallback((segs: string[]) => {
    onFocusSegmentsRef.current(sanitizeTreeFocusPath(listRef.current, segs))
  }, [])

  const { displayRoots } = getTreeDrillDownView(list, focusSegments)

  if (loading && nodes === undefined) {
    return (
      <div className="px-3 py-1 text-xs text-muted-foreground/50 italic" data-testid={testIds.explorerSection.container}>
        Loading…
      </div>
    )
  }

  return (
    <div
      className="relative min-w-0 max-w-full"
      data-testid={testIds.explorerSection.container}
      data-explorer-refreshing={refreshing ? 'true' : undefined}
    >
      {refreshing && (
        <div
          className="pointer-events-none absolute inset-0 z-[1] flex items-start justify-end px-2 pt-1"
          aria-hidden
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground/60" />
        </div>
      )}
      {focusSegments.length > 0 && (
        <div
          className="mb-1 block px-2 py-1 text-[11px] leading-snug text-sidebar-foreground/90"
          role="navigation"
          aria-label="Folder path"
          data-testid={testIds.explorerSection.breadcrumbs}
        >
          {focusSegments.map((seg, i) => {
            const pathUpToHere = focusSegments.slice(0, i + 1)
            const isLast = i === focusSegments.length - 1
            return (
              <span
                key={`bc-${treeKey}-${i}-${seg}`}
                className="inline-flex items-baseline gap-1 whitespace-nowrap align-middle pr-1 last:pr-0"
              >
                <span className="shrink-0 text-muted-foreground/40 select-none" aria-hidden>
                  /
                </span>
                {isLast ? (
                  <span
                    aria-current="location"
                    className="max-w-[min(100%,12rem)] truncate rounded-md px-2 py-0.5 text-xs font-medium text-sidebar-foreground"
                    title={seg}
                  >
                    {seg}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleFocusCrumb(pathUpToHere)}
                    title={`Focus ${pathUpToHere.join(' / ')}`}
                    className={workspaceCrumbParentButtonClassName}
                  >
                    {seg}
                  </button>
                )}
              </span>
            )
          })}
        </div>
      )}

      <div className="min-w-0" data-testid={testIds.explorerSection.tree}>
        {displayRoots.length > 0 ? (
          displayRoots.map(node => (
            <ExplorerInlineFolderNode
              key={node.id}
              node={node}
              depth={0}
              treeRoot={treeRoot}
              wsColor={wsColor}
              activeDocPath={activeDocumentPath}
              onNodeClick={handleNodeClick}
              onNodeDoubleClick={handleNodeDoubleClick}
              onContextMenu={onContextMenu ? handleContextMenu : undefined}
              onFolderNativeFileDrop={(folderPath, files) => {
                window.dispatchEvent(
                  new CustomEvent('import-text-drop-at-path', {
                    detail: {
                      folderPath,
                      files,
                      ...(workspaceId ? { workspaceId } : {}),
                    },
                  }),
                )
              }}
              onDrillFocus={handleDrillFocus}
              drilledDown={focusSegments.length > 0}
              openTabPaths={openTabPaths}
              openTabIndex={openTabIndex}
            />
          ))
        ) : (
          <div className="px-3 py-2 space-y-2" data-testid={testIds.explorerSection.emptyActions}>
            <p className="text-sm italic text-muted-foreground/60">
              {focusSegments.length > 0 ? 'Empty folder' : emptyLabel}
            </p>
            {treeRoot?.trim() ? (
              <CreateInFolderDropdown
                variant="empty"
                triggerTestId={testIds.explorerSection.emptyNewMenu}
                itemTestIds={{ import: testIds.explorerSection.emptyImportTextButton }}
                onAction={(id) =>
                  dispatchCreateInFolder(id, {
                    folderPath: treeRoot,
                    workspaceId,
                  })
                }
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
